import { useState, useEffect } from "react";
import type { TimelineClip } from "@scroll/shared-types/timeline";
import { getClipDuration } from "@scroll/shared-types/timeline-utils";
import { extractThumbnails, type ThumbnailFrame } from "@/lib/thumbnail-extractor";

interface ClipThumbnailStripProps {
  clip: TimelineClip;
  pixelsPerSecond: number;
  isSelected: boolean;
  onClick: () => void;
}

export default function ClipThumbnailStrip({
  clip,
  pixelsPerSecond,
  isSelected,
  onClick,
}: ClipThumbnailStripProps) {
  const [thumbnails, setThumbnails] = useState<ThumbnailFrame[]>([]);
  const duration = getClipDuration(clip);
  const width = duration * pixelsPerSecond;

  // Extract thumbnails on mount
  useEffect(() => {
    if (clip.type === "image") {
      // For images, just use the source as a single thumbnail
      setThumbnails([{ time: 0, dataUrl: clip.sourceUri }]);
      return;
    }

    const abortController = new AbortController();
    const frameCount = Math.max(3, Math.min(15, Math.ceil(duration / 2)));

    extractThumbnails(clip.sourceUri, clip.sourceDuration, frameCount, abortController.signal).then(
      (frames) => {
        if (!abortController.signal.aborted) setThumbnails(frames);
      },
    );

    return () => {
      abortController.abort();
    };
    // Thumbnails depend on the source media, not on trim/speed changes
  }, [clip.sourceUri, clip.sourceDuration, clip.type]);

  return (
    <div
      className={`timeline-clip ${isSelected ? "selected" : ""}`}
      style={{ width: `${width}px` }}
      onClick={onClick}
    >
      <div className="timeline-clip-thumbnails">
        {thumbnails.length > 0 ? (
          thumbnails.map((frame, i) => (
            <img
              key={i}
              src={frame.dataUrl}
              alt=""
              className="timeline-clip-thumbnail"
              style={{ width: `${width / thumbnails.length}px` }}
              draggable={false}
            />
          ))
        ) : (
          // Loading placeholder
          <div
            className="h-full bg-(--scroll-bg-tertiary) animate-pulse"
            style={{ width: `${width}px` }}
          />
        )}
      </div>
    </div>
  );
}
